import type { TokenCredential } from "@azure/identity";
import { LocalCliError } from "./errors.js";

const FOUNDRY_SCOPE = "https://ai.azure.com/.default";

export interface McpToolDescriptor {
  type: "mcp";
  server_label: string;
  server_url: string;
  project_connection_id: string;
  require_approval: "never" | "always";
  allowed_tools: { tool_names: string[] } | string[];
}

export interface AgentDefinition {
  kind: "prompt" | "hosted";
  model?: string;
  instructions?: string;
  reasoning?: { effort?: "low" | "medium" | "high" };
  tools?: (McpToolDescriptor | { type: string; [k: string]: unknown })[];
  [extra: string]: unknown;
}

export interface AgentVersionRecord {
  definition: AgentDefinition;
  metadata?: Record<string, string>;
  version: string;
  status?: string;
}

/**
 * GET the LATEST version of a Foundry prompt agent via REST. Returns the
 * version's definition + metadata as-is. Used by the brain link/unlink
 * cascades to read the current state before composing the update.
 *
 * Why not the SDK: project.agents.getVersion(name, 'latest') would be
 * cleaner, but the SDK doesn't expose the raw definition + metadata shape
 * we need to clone+swap for brain-rotation. The REST endpoint returns
 * exactly the shape we PATCH back via createVersion, no transform.
 */
export async function getAgentVersion(args: {
  credential: TokenCredential;
  projectEndpoint: string;
  agentName: string;
}): Promise<AgentVersionRecord> {
  const token = await args.credential.getToken(FOUNDRY_SCOPE);
  if (!token?.token) {
    throw new LocalCliError({
      code: "FOUNDRY_AUTH_FAILED",
      message: "Could not acquire Foundry data-plane token (scope: https://ai.azure.com/.default).",
    });
  }
  const url = `${args.projectEndpoint}/agents/${args.agentName}?api-version=v1`;
  const res = await fetch(url, {
    method: "GET",
    headers: { Authorization: `Bearer ${token.token}` },
  });
  if (res.status === 404) {
    throw new LocalCliError({
      code: "AGENT_NOT_FOUND",
      message: `Agent '${args.agentName}' not found at ${args.projectEndpoint}.`,
    });
  }
  if (!res.ok) {
    const text = await res.text();
    throw new LocalCliError({
      code: "AGENT_GET_FAILED",
      message: `GET ${url} returned HTTP ${res.status.toString()}: ${text.slice(0, 300)}`,
    });
  }
  const data = (await res.json()) as { versions?: { latest?: AgentVersionRecord } };
  const latest = data.versions?.latest;
  if (!latest) {
    throw new LocalCliError({
      code: "AGENT_GET_NO_LATEST",
      message: `GET ${url} returned no versions.latest`,
    });
  }
  return latest;
}
